import React from "react";
import { ArrowUpRight } from "lucide-react";

const PARTNERS = [
  {
    name: "FritsJurgens",
    blurb: "Invisible pivot door hinges. The Netherlands.",
    logo: "/viewlux/partners/fritsjurgens.svg",
    href: "https://www.fritsjurgens.com/",
  },
  {
    name: "Roto Frank",
    blurb: "Precision window & door hardware. Germany.",
    logo: "/viewlux/partners/roto-frank.png",
    href: "https://www.roto-frank.com/en/",
  },
  {
    name: "Storo",
    blurb: "Aluminum systems & extrusions. Italy.",
    logo: "/viewlux/partners/storo.png",
    href: "https://www.storo.it/",
  },
  {
    name: "Technoform",
    blurb: "Thermal break technology. Germany.",
    logo: "/viewlux/partners/technoform.svg",
    href: "https://www.technoform.com/en",
  },
  {
    name: "Molimo Labs",
    blurb: "Independent product testing & certification.",
    logo: "/viewlux/partners/molimo.svg",
    href: "https://molimo.com/",
  },
];

export default function PartnerLogos() {
  return (
    <section
      data-testid="partners-section"
      id="partners"
      className="relative bg-[#16332d] text-white border-y border-[#c99658]/15"
    >
      <div className="container-vlx py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-5">
            <span className="eyebrow eyebrow-light">Engineered Together</span>
            <div className="mt-4 rule-gold" />
            <h2 className="heading-lg mt-8 text-[30px] md:text-[44px] text-white leading-[1.1]">
              The hardware behind{" "}
              <em className="italic text-[#e2b880] font-light">
                every ViewLux opening.
              </em>
            </h2>
            <p className="mt-6 text-white/70 text-[14.5px] leading-[1.9] font-nunito max-w-md">
              Every window and door we build is engineered with components from
              the world&rsquo;s most respected manufacturers &mdash; chosen for
              precision, longevity, and quiet performance. We collaborate
              directly with each partner to specify the right system for your
              project.
            </p>
          </div>

          <div className="md:col-span-7 md:pt-2">
            <ul className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-[#c99658]/15">
              {PARTNERS.map((p) => (
                <li key={p.name} className="bg-[#16332d]">
                  <a
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`partner-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                    aria-label={`Visit ${p.name} — ${p.blurb}`}
                    className="group flex flex-col items-center text-center justify-between h-full px-4 lg:px-5 py-10 md:py-12 transition-colors duration-500 hover:bg-[#0f2420]"
                  >
                    <div className="h-12 md:h-14 flex items-center justify-center w-full">
                      <img
                        src={p.logo}
                        alt={`${p.name} logo`}
                        loading="lazy"
                        decoding="async"
                        className="max-h-full max-w-[120px] w-auto object-contain transition-all duration-500 filter brightness-0 invert opacity-70 group-hover:opacity-100 group-hover:brightness-0 group-hover:invert group-hover:scale-[1.04]"
                      />
                    </div>
                    <div className="mt-8">
                      <div className="font-cormorant italic text-[#e2b880] text-[20px] leading-tight">
                        {p.name}
                      </div>
                      <p className="mt-2 text-white/55 text-[12.5px] leading-[1.6] font-nunito">
                        {p.blurb}
                      </p>
                      <span className="mt-4 inline-flex items-center gap-1 text-[10px] tracking-[0.4em] uppercase font-raleway text-[#e2b880]/80 group-hover:text-white transition-colors">
                        Visit
                        <ArrowUpRight className="w-3 h-3" />
                      </span>
                    </div>
                  </a>
                </li>
              ))}
              {/* Filler tile so the orphan grid slot doesn't show the bronze divider background on mobile/tablet. Hidden on lg where 5 cards perfectly fill 5 columns. */}
              <li
                aria-hidden="true"
                className="bg-[#16332d] lg:hidden"
              />
            </ul>
            <p className="mt-6 text-[11px] tracking-[0.35em] uppercase font-raleway text-[#e2b880]">
              Names &amp; logos are property of their respective owners.
            </p>
          </div>
        </div>

        {/* Hardware finishes video — looped showcase of door handle in every available color */}
        <div className="mt-20 md:mt-28 grid md:grid-cols-12 gap-10 md:gap-16 items-center border-t border-[#c99658]/15 pt-16 md:pt-20">
          <div className="md:col-span-5 order-2 md:order-1">
            <span className="eyebrow eyebrow-light">Hardware Finishes</span>
            <div className="mt-4 rule-gold" />
            <h3 className="heading-lg mt-8 text-[28px] md:text-[40px] text-white leading-[1.1]">
              The hardware in{" "}
              <em className="italic font-light text-[#e2b880]">your finish.</em>
            </h3>
            <p className="mt-6 text-white/65 text-[14.5px] leading-[1.9] font-nunito max-w-md">
              Every ViewLux handle, hinge, and lock is available in our full
              palette of architectural finishes &mdash; matte black, bronze,
              satin nickel, brushed aluminum, and a curated range of whites
              and creams. Pick one to match the frame, contrast it, or specify
              a different finish inside vs outside.
            </p>
            <p className="mt-6 text-[10.5px] tracking-[0.4em] uppercase font-raleway text-[#e2b880]">
              13+ Standard Finishes &middot; Custom RAL Available
            </p>
          </div>

          <div className="md:col-span-7 order-1 md:order-2">
            {/* Cream panel — the source video's white studio background blends almost seamlessly with this site cream so the handle appears to float on a clean editorial backdrop. */}
            <div className="relative bg-cream border border-[#c99658]/25 overflow-hidden aspect-[4/3] max-w-2xl mx-auto md:ml-auto md:mr-0">
              <video
                data-testid="hardware-finishes-video"
                src="/viewlux/video/hardware-finishes.mp4"
                poster="/viewlux/video/hardware-finishes-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                disablePictureInPicture
                aria-label="Door handle hardware shown in 13 available finishes"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <span className="absolute top-4 left-4 z-10 text-[10px] tracking-[0.4em] uppercase font-raleway text-[#7a5b2f] bg-cream/80 backdrop-blur px-3 py-1.5">
                Finishes · Loop
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
